import React, { useEffect, useState } from 'react';
import { PUBLIC_URL } from '../config/env';
import './PrivacyPolicy.css';

/** Minimal markdown → React nodes (headings, paragraphs, lists, tables, bold, links). */
function renderMarkdown(md) {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  const inline = (text) => {
    const parts = [];
    const re = /(\*\*[^*]+\*\*|\[([^\]]+)\]\(([^)]+)\))/g;
    let last = 0;
    let m;
    let key = 0;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) parts.push(text.slice(last, m.index));
      if (m[0].startsWith('**')) {
        parts.push(<strong key={key++}>{m[0].slice(2, -2)}</strong>);
      } else {
        const href = m[3];
        const external = /^https?:\/\//.test(href);
        parts.push(
          <a
            key={key++}
            href={href}
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            {m[2]}
          </a>
        );
      }
      last = m.index + m[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
  };

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }
    if (line.trim() === '---') {
      blocks.push(<hr key={`hr-${i}`} />);
      i += 1;
      continue;
    }
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const Tag = level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3';
      blocks.push(<Tag key={`h-${i}`}>{inline(heading[2])}</Tag>);
      i += 1;
      continue;
    }
    if (line.trim().startsWith('|') && i + 1 < lines.length && /^\|?\s*-+/.test(lines[i + 1].trim())) {
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        const cells = lines[i].trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
        if (!/^[-:\s|]+$/.test(lines[i].replace(/\|/g, ''))) {
          rows.push(cells);
        }
        i += 1;
      }
      if (rows.length) {
        const [header, ...body] = rows;
        blocks.push(
          <div key={`t-${i}`} className="ae-privacy-table-wrap">
            <table>
              <thead>
                <tr>
                  {header.map((c, idx) => (
                    <th key={idx}>{inline(c)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {body.map((row, rIdx) => (
                  <tr key={rIdx}>
                    {row.map((c, cIdx) => (
                      <td key={cIdx}>{inline(c)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }
    if (/^[-*]\s+/.test(line.trim())) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ''));
        i += 1;
      }
      blocks.push(
        <ul key={`ul-${i}`}>
          {items.map((item, idx) => (
            <li key={idx}>{inline(item)}</li>
          ))}
        </ul>
      );
      continue;
    }
    if (/^\d+\.\s+/.test(line.trim())) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ''));
        i += 1;
      }
      blocks.push(
        <ol key={`ol-${i}`}>
          {items.map((item, idx) => (
            <li key={idx}>{inline(item)}</li>
          ))}
        </ol>
      );
      continue;
    }
    const para = [line];
    i += 1;
    while (i < lines.length && lines[i].trim() && !/^(#{1,3}\s|---|[-*]\s|\d+\.\s|\|)/.test(lines[i])) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push(<p key={`p-${i}`}>{inline(para.join(' '))}</p>);
  }
  return blocks;
}

export default function PrivacyPolicy() {
  const [markdown, setMarkdown] = useState<any>('');
  const [error, setError] = useState<any>('');

  useEffect(() => {
    let cancelled = false;
    fetch(`${PUBLIC_URL}/privacy-policy.md`, { cache: 'no-cache' })
      .then((r) => {
        if (!r.ok) throw new Error(`Could not load privacy policy (${r.status})`);
        return r.text();
      })
      .then((text) => {
        if (!cancelled) setMarkdown(text);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load privacy policy');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="ae-privacy-page">
      <div className="ae-privacy-shell">
        <div className="ae-privacy-brand">
          <img src="/academic-eye-logo.png" alt="Academic Eye Logo" />
          <div className="ae-privacy-brand-name">
            <span>Academic Eye</span>
          </div>
        </div>

        <section className="ae-privacy-card">
          <a href="/login" className="ae-privacy-back">
            <span aria-hidden="true">← </span>Back to sign in
          </a>

          {error && (
            <p className="ae-privacy-error" role="alert">
              {error}. Contact ashley@academiceye.net for a copy of our privacy practices.
            </p>
          )}
          {!error && !markdown && <p className="ae-privacy-status">Loading privacy policy…</p>}
          {!error && markdown && (
            <article className="ae-privacy-policy">{renderMarkdown(markdown)}</article>
          )}

          <p className="ae-privacy-footer">
            Questions: <a href="mailto:ashley@academiceye.net">ashley@academiceye.net</a>
          </p>
        </section>
      </div>
    </main>
  );
}
